import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CheckCircle, CreditCard, XCircle } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import {
  createAdminPlan,
  fetchAdminFeatures,
  fetchAdminPlans,
  updateAdminPlan,
  type FeatureDisplay,
  type FeatureRegistryItem,
  type PlanoAdmin,
} from '../../api/client';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { PageHeader } from '../../components/ui';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Separator } from '../../components/ui/separator';
import { Spinner } from '../../components/ui/spinner';
import { Switch } from '../../components/ui/switch';
import { useNotification } from '../../context/NotificationProvider';
import { classifyError, notifyMutationError } from '../../lib/errors';

interface FeatureDisplayItem {
  text: string;
  included: boolean;
  /** Chave que vincula ao feature_registry. Ausente = texto extra sem feature técnica. */
  _featureKey?: string;
}

/**
 * Gera texto sugerido para a landing page a partir do limite técnico.
 * DEVE ser consistente com _build_features_display() do seed (backend/app/scripts/seed.py).
 */
function suggestDisplayText(feature: FeatureRegistryItem, value: unknown): string {
  const chave = feature.chave_limites;

  if (feature.tipo === 'boolean') {
    return feature.nome;
  }

  // quota com valor nulo
  if (value === null || value === undefined) {
    if (feature.valor_nulo_significa === 'ilimitado') {
      return `${feature.nome} ilimitados`;
    }
    return feature.nome;
  }

  // quota com valor numérico — tratamentos especiais por chave
  const num = typeof value === 'number' ? value : parseInt(String(value));

  if (chave === 'storage_mb') {
    return num >= 1024
      ? `${Math.floor(num / 1024)} GB de armazenamento`
      : `${num} MB de armazenamento`;
  }
  if (chave === 'djen_sync_intervalo_horas') {
    return `Sincronização a cada ${num}h`;
  }
  if (chave === 'djen_meses_historico') {
    return `${num} meses de histórico`;
  }
  if (chave === 'ia_quota_creditos') {
    // Cota em créditos (docs/PLANO_CREDITOS_IA.md), não em reais.
    return `Até ${num.toLocaleString('pt-BR')} créditos de IA por mês`;
  }

  // quota genérica
  if (feature.unidade) {
    return `Até ${num} ${feature.unidade}`;
  }
  return `${num}`;
}

// =============================================================
// Wrapper: carrega dados, resolve loading/404, monta o form
// =============================================================

export default function PlanDetailPage() {
  const { id } = useParams<{ id: string }>();
  const isNew = id === 'novo';

  const { data: plans, isLoading: plansLoading, error: plansError } = useQuery({
    queryKey: ['admin', 'plans'],
    queryFn: fetchAdminPlans,
  });

  const { data: features, error: featuresError } = useQuery({
    queryKey: ['admin', 'features'],
    queryFn: fetchAdminFeatures,
  });

  const loadError = plansError || featuresError;

  // Erro ao carregar dados (qualquer uma das queries falhou sem dados)
  if (loadError && (!plans || !features)) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Erro ao carregar"
          icon={<CreditCard />}
          backTo={{ label: 'Planos', path: '/app/admin/planos' }}
        />
        <Alert variant="destructive">
          <AlertDescription>{classifyError(loadError).message}</AlertDescription>
        </Alert>
      </div>
    );
  }

  // Loading — esperar ambos (plans + features) para inicializar o form corretamente
  if (plansLoading || !plans || !features) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Carregando..."
          icon={<CreditCard />}
          backTo={{ label: 'Planos', path: '/app/admin/planos' }}
        />
        <div className="flex justify-center py-12">
          <Spinner size={32} label="Carregando detalhes" />
        </div>
      </div>
    );
  }

  const plan = isNew ? null : plans.find((p) => p.id === id);

  // 404
  if (!isNew && !plan) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Plano não encontrado"
          icon={<CreditCard />}
          backTo={{ label: 'Planos', path: '/app/admin/planos' }}
        />
        <Card className="p-8 text-center text-muted-foreground">
          O plano solicitado não existe ou foi removido.
        </Card>
      </div>
    );
  }

  // key garante remount se navegar de um plano para outro
  return (
    <PlanForm
      key={plan?.id ?? 'novo'}
      plan={plan ?? null}
      plans={plans}
      features={features ?? []}
    />
  );
}

// =============================================================
// Form: recebe dados prontos, useState funciona sem useEffect
// =============================================================

interface PlanFormProps {
  plan: PlanoAdmin | null;
  plans: PlanoAdmin[];
  features: FeatureRegistryItem[];
}

function PlanForm({ plan, plans, features }: PlanFormProps) {
  const isNew = !plan;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotification();

  const availableGroups = [...new Set(plans.map((p) => p.grupo))].sort();
  const sortedFeatures = [...features].sort((a, b) => a.ordem - b.ordem);

  // Defaults para criação
  const defaultOrder = (() => {
    const sorted = [...plans].sort((a, b) => a.ordem_exibicao - b.ordem_exibicao);
    return sorted.length > 0 ? sorted[sorted.length - 1].ordem_exibicao + 1 : 0;
  })();

  // --- Form state (inicializado direto, sem useEffect) ---

  const [codigo, setCodigo] = useState(plan?.codigo ?? '');
  const [nome, setNome] = useState(plan?.nome ?? '');
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [descricao, setDescricao] = useState(plan?.descricao ?? '');
  const [precoCentavos, setPrecoCentavos] = useState(plan?.preco_centavos ?? 0);
  const [intervalo, setIntervalo] = useState(plan?.intervalo ?? 'MENSAL');
  const [trialDias, setTrialDias] = useState<number | null>(plan?.trial_dias ?? null);
  const [ordemExibicao, setOrdemExibicao] = useState(plan?.ordem_exibicao ?? defaultOrder);
  const [grupo, setGrupo] = useState(plan?.grupo ?? availableGroups[0] ?? '');
  const [stripePriceId, setStripePriceId] = useState(plan?.stripe_price_id ?? '');
  const [destaque, setDestaque] = useState(plan?.destaque ?? false);
  const [badgeText, setBadgeText] = useState(plan?.badge_text ?? '');
  const [ctaText, setCtaText] = useState(plan?.cta_text ?? '');
  // Inicializar features_display indexado por chave do registry.
  // Dados legados (sem _featureKey) são vinculados por posição na ordem do registry.
  const [featuresDisplay, setFeaturesDisplay] = useState<Record<string, FeatureDisplayItem>>(() => {
    const existing = plan?.features_display ?? [];
    const result: Record<string, FeatureDisplayItem> = {};
    // Mapear por posição: feature[i] do registry → features_display[i] do plano
    sortedFeatures.forEach((feat, i) => {
      if (i < existing.length) {
        result[feat.chave_limites] = {
          text: existing[i].text,
          included: existing[i].included,
        };
      }
    });
    return result;
  });
  const [limites, setLimites] = useState<Record<string, unknown>>(plan?.limites ?? {});

  // --- Save ---

  const saveMutation = useMutation({
    mutationFn: () => {
      const payload: Partial<PlanoAdmin> = {
        ...(isNew ? { codigo } : {}),
        nome,
        descricao: descricao || null,
        preco_centavos: precoCentavos,
        intervalo,
        trial_dias: trialDias,
        ordem_exibicao: ordemExibicao,
        grupo,
        stripe_price_id: stripePriceId || null,
        destaque,
        badge_text: badgeText || null,
        cta_text: ctaText || null,
        features_display: sortedFeatures
          .filter((f) => featuresDisplay[f.chave_limites]?.text)
          .map((f) => ({
            text: featuresDisplay[f.chave_limites].text,
            included: featuresDisplay[f.chave_limites].included,
          })) as FeatureDisplay[],
        limites,
      };
      return isNew ? createAdminPlan(payload) : updateAdminPlan(plan!.id, payload);
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'plans'] });
      showSuccess(isNew ? 'Plano criado' : 'Plano salvo');
      if (isNew) {
        navigate(`/app/admin/planos/${result.id}`, { replace: true });
      }
    },
    onError: (err) => notifyMutationError(err, showError),
  });

  const handleSave = () => {
    const errors: Record<string, string> = {};
    if (isNew && !codigo.trim()) {
      errors.codigo = 'Código é obrigatório';
    }
    if (!nome.trim()) {
      errors.nome = 'Nome é obrigatório';
    }
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      document.getElementById(errors.codigo ? 'plano-codigo' : 'plano-nome')?.focus();
      return;
    }
    setFormErrors({});
    saveMutation.mutate();
  };

  // --- Features Display helpers ---

  const updateFeatureDisplayByKey = (key: string, field: 'text' | 'included', value: string | boolean) => {
    setFeaturesDisplay((prev) => ({
      ...prev,
      [key]: { ...prev[key], [field]: value },
    }));
  };

  const toggleFeatureDisplay = (key: string, feature: FeatureRegistryItem) => {
    setFeaturesDisplay((prev) => {
      const existing = prev[key];
      if (existing) {
        return { ...prev, [key]: { ...existing, included: !existing.included } };
      }
      // Criar novo com texto sugerido
      return {
        ...prev,
        [key]: { text: suggestDisplayText(feature, limites[key]), included: true },
      };
    });
  };

  // --- Limites helpers ---

  const updateLimite = (key: string, value: unknown) => {
    setLimites((prev) => ({ ...prev, [key]: value }));

    // Atualizar texto e included automaticamente
    const feature = sortedFeatures.find((f) => f.chave_limites === key);
    if (feature) {
      const newText = suggestDisplayText(feature, value);
      let included: boolean;
      if (feature.tipo === 'boolean') {
        included = value === true;
      } else {
        // quota: null com null=desabilitado → desativado; null com
        // null=ilimitado ou null=padrao (herda o teto da plataforma) → ativado
        if (value === null || value === undefined) {
          included = ['ilimitado', 'padrao'].includes(feature.valor_nulo_significa);
        } else {
          included = true;
        }
      }
      setFeaturesDisplay((prev) => ({
        ...prev,
        [key]: { text: newText, included },
      }));
    }
  };

  const renderLimiteInput = (feature: FeatureRegistryItem) => {
    const key = feature.chave_limites;
    const currentValue = limites[key];

    if (feature.tipo === 'boolean') {
      return (
        <Switch
          checked={currentValue === true}
          onCheckedChange={(checked) => updateLimite(key, checked)}
        />
      );
    }

    const isNull = currentValue === null || currentValue === undefined;
    return (
      <div className="flex items-center gap-2">
        <Input
          type="number"
          value={isNull ? '' : String(currentValue)}
          onChange={(e) => {
            const v = e.target.value;
            updateLimite(key, v === '' ? null : parseInt(v));
          }}
          placeholder={
            feature.valor_nulo_significa === 'ilimitado'
              ? 'Ilimitado'
              : feature.valor_nulo_significa === 'padrao'
                ? 'Padrão'
                : 'Desabilitado'
          }
          className="h-8 w-24 text-sm"
        />
        {feature.unidade && (
          <span className="text-xs text-muted-foreground whitespace-nowrap">{feature.unidade}</span>
        )}
      </div>
    );
  };

  // --- Render ---

  const pageTitle = isNew ? 'Novo Plano' : plan!.nome;
  const pageSubtitle = isNew
    ? 'Preencha os dados e configure features e limites.'
    : `Código: ${plan!.codigo} · Grupo: ${plan!.grupo}`;

  return (
    <div className="space-y-6">
      <PageHeader
        title={pageTitle}
        subtitle={pageSubtitle}
        icon={<CreditCard />}
        backTo={{ label: 'Planos', path: '/app/admin/planos' }}
        actions={
          <Button onClick={handleSave} disabled={saveMutation.isPending}>
            {saveMutation.isPending ? 'Salvando...' : isNew ? 'Criar Plano' : 'Salvar Alterações'}
          </Button>
        }
      />

      {/* Dados Gerais */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Dados Gerais</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {isNew && (
              <div>
                <Label htmlFor="plano-codigo">Código</Label>
                <Input
                  id="plano-codigo"
                  autoFocus
                  value={codigo}
                  onChange={(e) => {
                    setCodigo(e.target.value);
                    if (formErrors.codigo) setFormErrors((prev) => ({ ...prev, codigo: '' }));
                  }}
                  placeholder="Ex: premium_anual"
                  className={formErrors.codigo ? 'border-destructive' : ''}
                  aria-invalid={!!formErrors.codigo}
                />
                {formErrors.codigo && <p className="mt-1 text-xs text-destructive">{formErrors.codigo}</p>}
              </div>
            )}
            <div>
              <Label htmlFor="plano-nome">Nome</Label>
              <Input
                id="plano-nome"
                autoFocus={!isNew}
                value={nome}
                onChange={(e) => {
                  setNome(e.target.value);
                  if (formErrors.nome) setFormErrors((prev) => ({ ...prev, nome: '' }));
                }}
                className={formErrors.nome ? 'border-destructive' : ''}
                aria-invalid={!!formErrors.nome}
              />
              {formErrors.nome && <p className="mt-1 text-xs text-destructive">{formErrors.nome}</p>}
            </div>
            <div>
              <Label>Grupo</Label>
              <Select value={grupo} onValueChange={setGrupo}>
                <SelectTrigger><SelectValue placeholder="Selecione o grupo" /></SelectTrigger>
                <SelectContent>
                  {availableGroups.map((g) => (
                    <SelectItem key={g} value={g}>{g}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Intervalo</Label>
              <Select value={intervalo} onValueChange={setIntervalo}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="MENSAL">Mensal</SelectItem>
                  <SelectItem value="ANUAL">Anual</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label>Descrição</Label>
            <Input value={descricao} onChange={(e) => setDescricao(e.target.value)} placeholder="Descrição do plano" />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <Label>Preço (centavos)</Label>
              <Input type="number" value={precoCentavos} onChange={(e) => setPrecoCentavos(parseInt(e.target.value) || 0)} />
            </div>
            <div>
              <Label>Trial (dias)</Label>
              <Input
                type="number"
                value={trialDias ?? ''}
                onChange={(e) => setTrialDias(e.target.value ? parseInt(e.target.value) : null)}
                placeholder="Sem trial"
              />
            </div>
            <div>
              <Label>Ordem de exibição</Label>
              <Input type="number" value={ordemExibicao} onChange={(e) => setOrdemExibicao(parseInt(e.target.value) || 0)} />
            </div>
            <div>
              <Label>Stripe Price ID</Label>
              <Input value={stripePriceId} onChange={(e) => setStripePriceId(e.target.value)} placeholder="price_..." />
            </div>
          </div>

          <Separator />

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="flex items-center gap-2">
              <Switch checked={destaque} onCheckedChange={setDestaque} />
              <Label>Plano em destaque</Label>
            </div>
            <div>
              <Label>Badge (landing page)</Label>
              <Input value={badgeText} onChange={(e) => setBadgeText(e.target.value)} placeholder="Ex: Mais popular" />
            </div>
            <div>
              <Label>CTA (landing page)</Label>
              <Input value={ctaText} onChange={(e) => setCtaText(e.target.value)} placeholder="Ex: Começar agora" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Features — limite técnico + texto de marketing unificados */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
          <div>
            <CardTitle className="text-base">Features e Limites</CardTitle>
            <p className="mt-1 text-xs text-muted-foreground">
              Configure o limite técnico e o texto exibido na landing page para cada feature.
            </p>
          </div>
        </CardHeader>
        <CardContent>
          {sortedFeatures.length === 0 ? (
            <p className="py-4 text-center text-sm text-muted-foreground">
              Nenhuma feature no registry. Configure em Features primeiro.
            </p>
          ) : (
            <div className="space-y-1">
              {sortedFeatures.map((feature) => {
                const key = feature.chave_limites;
                const displayItem = featuresDisplay[key];
                const isIncluded = displayItem?.included ?? false;
                const displayText = displayItem?.text ?? '';

                return (
                  <div
                    key={feature.id}
                    className="grid grid-cols-1 gap-3 rounded-lg border border-border p-3 sm:grid-cols-[1fr_auto_1fr]"
                  >
                    {/* Coluna 1: Nome + controle técnico */}
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-medium">{feature.nome}</p>
                        <p className="text-[11px] text-muted-foreground">
                          {feature.chave_limites}
                          {' · '}
                          null = {feature.valor_nulo_significa}
                        </p>
                      </div>
                      <div className="flex-shrink-0">
                        {renderLimiteInput(feature)}
                      </div>
                    </div>

                    {/* Separador vertical (desktop) */}
                    <div className="hidden sm:flex sm:items-center">
                      <Separator orientation="vertical" className="h-8" />
                    </div>

                    {/* Coluna 2: Texto de marketing */}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => toggleFeatureDisplay(key, feature)}
                        className="flex-shrink-0 cursor-pointer"
                        aria-label={isIncluded ? 'Incluído na landing' : 'Não incluído na landing'}
                      >
                        {isIncluded ? (
                          <CheckCircle size={18} className="text-emerald-500" />
                        ) : (
                          <XCircle size={18} className="text-muted-foreground/30" />
                        )}
                      </button>

                      <Input
                        value={displayText}
                        onChange={(e) => {
                          if (displayItem) {
                            updateFeatureDisplayByKey(key, 'text', e.target.value);
                          } else {
                            // Criar ao digitar
                            setFeaturesDisplay((prev) => ({
                              ...prev,
                              [key]: { text: e.target.value, included: true },
                            }));
                          }
                        }}
                        placeholder={suggestDisplayText(feature, limites[key])}
                        className="h-8 flex-1 text-sm"
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Botão salvar no rodapé */}
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saveMutation.isPending}>
          {saveMutation.isPending ? 'Salvando...' : isNew ? 'Criar Plano' : 'Salvar Alterações'}
        </Button>
      </div>
    </div>
  );
}
